// Encrypted Inventory Service for SAM
import { apiService } from './api';
import { EncryptionService } from './encryptionService';

export interface InventoryItem {
  [key: string]: any;
}

export interface SaveInventoryRequest {
  inventory_type: string;
  version: string;
  items: InventoryItem[];
  encryptedData?: string;
  encrypted?: boolean;
}

class InventoryService {
  /**
   * Save inventory data with encryption
   */
  static async saveInventory(inventoryType: string, version: string, items: InventoryItem[]): Promise<void> {
    try {
      // Separate sensitive inventory data from metadata
      const sensitiveData = {
        items: items.filter(item => item._type !== 'completion_status') // Don't encrypt completion status
      };
      
      // Check if we have sensitive data to encrypt
      const hasSensitiveData = items.length > 0;
      
      let encryptedData: string | undefined;
      
      if (hasSensitiveData) {
        try {
          encryptedData = await EncryptionService.encrypt(sensitiveData);
        } catch (encryptionError: any) {
          // Provide more detailed error message
          const errorMessage = encryptionError?.message || String(encryptionError);
          if (errorMessage.includes('encryption key') || errorMessage.includes('Unable to access')) {
            throw new Error('Encryption key unavailable. Please refresh the page and try again. If the problem persists, try logging out and back in.');
          }
          throw new Error('Failed to encrypt inventory data. Please try again.');
        }
      }
      
      // Prepare the request with encrypted data
      const request: SaveInventoryRequest = {
        inventory_type: inventoryType,
        version,
        items: items.filter(item => item._type === 'completion_status'), // Only send non-sensitive items
        encryptedData,
        encrypted: !!encryptedData
      };
      
      await apiService.saveInventoryEncrypted(request);
    } catch (error) {
      // Silently handle errors
      throw error;
    }
  }

  /**
   * Get inventory data with decryption
   */
  static async getInventory(inventoryType: string, version: string): Promise<InventoryItem[]> {
    try {
      const response = await apiService.getInventoryEncrypted(inventoryType, version);
      
      if (response.encrypted && response.encryptedData) {
        try {
          const decryptedData = await EncryptionService.decrypt(response.encryptedData);
          
          // Combine decrypted items with any non-encrypted items (like completion status)
          const allItems = [
            ...(decryptedData.items || []),
            ...(response.items || [])
          ];
          
          return allItems;
        } catch (decryptionError) {
          // Silently handle decryption errors (likely old data with different encryption key)
          throw new Error('Failed to decrypt inventory data');
        }
      }
      
      // Return items as-is if not encrypted
      return response.items || [];
    } catch (error) {
      // Silently handle errors
      throw error;
    }
  }

  /**
   * Save inventory completion status (non-sensitive data)
   */
  static async saveInventoryCompletion(inventoryType: string, version: string, completed: boolean): Promise<void> {
    try {
      const completionItem = {
        _type: 'completion_status',
        completed,
        timestamp: new Date().toISOString()
      };
      
      // Get existing items
      const existingItems = await this.getInventory(inventoryType, version);
      
      // Remove any existing completion item
      const filteredItems = existingItems.filter(item => item._type !== 'completion_status');
      
      // Add the completion status
      const updatedItems = [...filteredItems, completionItem];
      
      // Save the updated inventory data
      await this.saveInventory(inventoryType, version, updatedItems);
    } catch (error) {
      // Silently handle errors
      throw error;
    }
  }

  /**
   * Get inventory completion status
   */
  static async getInventoryCompletion(inventoryType: string, version: string): Promise<{ completed: boolean }> {
    try {
      const items = await this.getInventory(inventoryType, version);
      const completionItem = items.find(item => item._type === 'completion_status');
      return { completed: completionItem?.completed || false };
    } catch (error) {
      // Silently handle errors
      return { completed: false };
    }
  }

  /**
   * Delete inventory
   */
  static async deleteInventory(inventoryType: string, version: string): Promise<void> {
    try {
      await apiService.deleteInventory(inventoryType, version);
    } catch (error) {
      // Silently handle errors
      throw error;
    }
  }

  /**
   * List inventory versions
   */
  static async listInventoryVersions(inventoryType: string): Promise<string[]> {
    try {
      return await apiService.listInventoryVersions(inventoryType);
    } catch (error) {
      // Silently handle errors
      throw error;
    }
  }
}

export const inventoryService = new InventoryService();
export { InventoryService }; 